var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import Config from './Config';
/**
 * Manages per-device consent state.
 *
 * State is persisted in localStorage so it survives page reloads.
 * ConsentManager does no networking itself: `onSync` fires when the change
 * (or the elapsed-resync check) says the current state needs to reach the
 * server, and the Tracker turns that into an ordinary `consent` event on the
 * same pipeline every other tracked event uses.
 */
var ConsentManager = /** @class */ (function () {
    function ConsentManager(onChange, onSync) {
        this.onChange = onChange;
        this.onSync = onSync;
    }
    /** Return the current consent state or null if never set. */
    ConsentManager.prototype.getConsent = function () {
        try {
            var raw = localStorage.getItem(Config.LS_CONSENT_KEY);
            return raw ? JSON.parse(raw) : null;
        }
        catch (_a) {
            return null;
        }
    };
    /**
     * Merge `purposes` into the stored state and persist locally. `onSync`
     * fires only when the merged state actually changes, or when it's been
     * more than CONSENT_RESYNC_INTERVAL_MS since the last sync (keeps the
     * server-side proof-of-consent record from going stale even across many
     * unchanged page loads) — never on every identical call. Partial updates
     * are supported: calling `setConsent({ analytics: true })` leaves other
     * purposes unchanged.
     */
    ConsentManager.prototype.setConsent = function (purposes) {
        var _a, _b, _c;
        var current = (_a = this.getConsent()) !== null && _a !== void 0 ? _a : {};
        var updated = __assign(__assign({}, current), purposes);
        try {
            localStorage.setItem(Config.LS_CONSENT_KEY, JSON.stringify(updated));
        }
        catch (_d) {
            // localStorage may be full or blocked in private browsing
        }
        (_b = this.onChange) === null || _b === void 0 ? void 0 : _b.call(this, updated);
        if (!this.hasSameState(current, updated) || this.isResyncDue()) {
            // Stamp before notifying (not after a response) so a failed send
            // downstream doesn't leave the next page load retrying immediately.
            try {
                localStorage.setItem(Config.LS_CONSENT_SYNCED_AT, Date.now().toString());
            }
            catch (_e) {
                // ignore
            }
            (_c = this.onSync) === null || _c === void 0 ? void 0 : _c.call(this, updated);
        }
    };
    /** Clear the locally stored consent state. */
    ConsentManager.prototype.clearConsent = function () {
        var _a;
        try {
            localStorage.removeItem(Config.LS_CONSENT_KEY);
        }
        catch (_b) {
            // ignore
        }
        (_a = this.onChange) === null || _a === void 0 ? void 0 : _a.call(this, null);
    };
    /** True when every purpose key present in either state agrees. */
    ConsentManager.prototype.hasSameState = function (a, b) {
        var keys = Object.keys(__assign(__assign({}, a), b));
        for (var _i = 0, keys_1 = keys; _i < keys_1.length; _i++) {
            var key = keys_1[_i];
            if (a[key] !== b[key])
                return false;
        }
        return true;
    };
    /** True when it's never been synced, or the resync interval has elapsed. */
    ConsentManager.prototype.isResyncDue = function () {
        try {
            var last = Number(localStorage.getItem(Config.LS_CONSENT_SYNCED_AT) || 0);
            return Date.now() - last >= Config.CONSENT_RESYNC_INTERVAL_MS;
        }
        catch (_a) {
            return true;
        }
    };
    return ConsentManager;
}());
export default ConsentManager;
